/* ============================================================================
   ef-motion.js — the reveal, the film, and the pinned scrub.

   Three behaviours, all progressive enhancement: with this file absent the
   page is still complete, because `.no-js` on <html> (removed below) resolves
   every hidden reveal state to visible.

   Deliberately dependency-free and ~4 KB. It runs on Shopify's Online Store
   2.0 section lifecycle, so it re-initialises when a merchant edits a section
   in the theme editor.
   ========================================================================== */
(function () {
  'use strict';

  var reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ---------------------------------------------------------- the reveal */
  function initReveals(root) {
    var targets = root.querySelectorAll('.ef-in, .ef-in-group');
    if (!targets.length) return;

    if (reduce || !('IntersectionObserver' in window)) {
      // No observer, or the reader asked for less motion: show everything.
      Array.prototype.forEach.call(targets, function (el) { el.classList.add('is-in'); });
      return;
    }
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) {
        if (!e.isIntersecting) return;
        e.target.classList.add('is-in');
        io.unobserve(e.target);          // reveal once, then stop watching
      });
    }, { rootMargin: '0px 0px -12% 0px', threshold: 0.1 });
    Array.prototype.forEach.call(targets, function (el) { io.observe(el); });
  }

  /* ------------------------------------------------------------ the film */
  /* Modelled on apple.com's InlineMedia lifecycle. The plugin chain there is
     LoadTimeout, ReplayOnlyAX, UnloadVideo, AnimLoad, AnimPlay,
     PictureToggleSource, ViewportSourceOnce — this implements the same five
     behaviours that matter, in ~60 lines:

       AnimLoad + ViewportSourceOnce  inject src on scroll-approach, ONCE
       AnimPlay                       play on viewport enter
       UnloadVideo                    clear src at end, reclaim memory
       LoadTimeout                    3s → fall back to the static frame
       ReplayOnlyAX                   reduced motion → still + Replay control

     The lazy src is the load-bearing one. `preload="none"` with NO `src` in the
     markup means a video-heavy page costs nothing until the reader approaches
     it. Apple injects at ~100vh above the viewport; so does this. */
  var LOAD_MARGIN = '100% 0px 100% 0px';   // ~1 viewport either side
  var LOAD_TIMEOUT = 3000;                  // apple.com's own data-load-timeout

  function tier() {
    // PictureToggleSource: pick the quality tier for the breakpoint.
    var w = window.innerWidth;
    if (w < 735) return 'small';
    if (w < 1441) return 'large';
    return 'xlarge';
  }

  function initFilm(root) {
    var films = root.querySelectorAll('[data-ef-film]');
    Array.prototype.forEach.call(films, function (film) {
      var video = film.querySelector('video');
      var replay = film.querySelector('.ef-film__replay');

      var onScroll = function () {
        film.classList.toggle('is-scrolled', window.scrollY > 40);
      };
      window.addEventListener('scroll', onScroll, { passive: true });
      onScroll();

      if (!video) return;

      // Reduced motion: never load the video at all. The still is the page.
      if (reduce) {
        if (replay) replay.hidden = false;
        return;
      }

      var loaded = false, timer = null;

      function load() {
        if (loaded) return;               // ViewportSourceOnce
        loaded = true;
        var src = video.getAttribute('data-src-' + tier()) ||
                  video.getAttribute('data-src');
        if (!src) return;

        // LoadTimeout: if it has not started by 3s, the reader keeps the still
        // rather than a black rectangle. A slow connection is the common case,
        // not the edge case.
        timer = window.setTimeout(function () {
          film.classList.add('is-timed-out');
        }, LOAD_TIMEOUT);

        video.addEventListener('loadeddata', function () {
          window.clearTimeout(timer);
          film.classList.remove('is-timed-out');
        }, { once: true });

        video.src = src;
        video.load();
      }

      function play() {
        var p = video.play();
        if (p && p.catch) p.catch(function () { /* refused; the still stands */ });
        film.classList.add('is-playing');
      }

      // UnloadVideo: most heroes play once. Releasing the source afterwards is
      // what keeps a page with seventeen videos from holding all of them.
      video.addEventListener('ended', function () {
        film.classList.add('is-ended');
        film.classList.remove('is-playing');
        if (video.hasAttribute('data-unload-at-end')) {
          video.removeAttribute('src');
          video.load();
          loaded = false;
        }
        if (replay) replay.hidden = false;
      });

      if (replay) {
        replay.addEventListener('click', function () {
          film.classList.remove('is-ended');
          replay.hidden = true;
          load();
          play();
        });
      }

      if (!('IntersectionObserver' in window)) { load(); return; }

      // AnimLoad — a wide margin, so the source is already in flight by the
      // time the section arrives.
      new IntersectionObserver(function (entries, obs) {
        entries.forEach(function (e) { if (e.isIntersecting) { load(); obs.disconnect(); } });
      }, { rootMargin: LOAD_MARGIN }).observe(film);

      // AnimPlay — actually on screen.
      new IntersectionObserver(function (entries) {
        entries.forEach(function (e) {
          if (e.isIntersecting) { load(); play(); }
          else { video.pause(); }
        });
      }, { threshold: 0.15 }).observe(film);
    });
  }

  /* ------------------------------------------------------------ the scrub */
  function initReels(root) {
    /* ---- lazy autoplay loops -------------------------------------------
       Decorative loops that are not [data-ef-film]: same idea, no replay UI.

       The home page shipped seven autoplaying <video> elements with real
       <source src> and preload="metadata". Measured on a cache-busted load at
       scrollY 0, all seven transferred — about 9MB — before the reader had
       scrolled anywhere, six of them below the fold and the largest roughly
       nine viewports down. On Malaysian 4G that is the arrival experience.

       Sources move to data-src and are assigned on approach. A video that
       never comes into view costs nothing but its poster. */
    root.querySelectorAll('[data-ef-lazyvideo]').forEach(function (video) {
      var started = false;
      function hydrate() {
        if (started) return;
        started = true;
        video.querySelectorAll('source[data-src]').forEach(function (sr) {
          sr.setAttribute('src', sr.getAttribute('data-src'));
          sr.removeAttribute('data-src');
        });
        video.load();
        var go = video.play();
        if (go && go.catch) go.catch(function () { /* autoplay refused; poster stands */ });
      }
      /* Reduced motion keeps the poster and downloads nothing: a decorative
         loop is exactly what that preference is asking us not to play. Only
         the no-IntersectionObserver fallback hydrates eagerly. */
      if (reduce) return;
      if (!('IntersectionObserver' in window)) { hydrate(); return; }
      new IntersectionObserver(function (entries, obs) {
        entries.forEach(function (e) {
          if (e.isIntersecting) { hydrate(); obs.disconnect(); }
        });
      }, { rootMargin: '200px 0px' }).observe(video);
    });

    var reels = root.querySelectorAll('[data-ef-reel]');
    Array.prototype.forEach.call(reels, function (reel) {
      var clips = reel.querySelectorAll('.ef-reel__clip');
      var caps  = reel.querySelectorAll('.ef-reel__cap');
      var ticks = reel.querySelectorAll('.ef-reel__tick');
      if (!caps.length) return;

      // Below 768px and under reduced motion the reel is unpinned by CSS and
      // every caption shows in flow, so there is nothing to drive.
      function active() { return !reduce && window.innerWidth >= 768; }

      function frame() {
        if (!running) return;
        scrub();
        window.requestAnimationFrame(frame);
      }

      function scrub() {
        if (!active()) return;
        var r = reel.getBoundingClientRect();
        var travel = r.height - window.innerHeight;
        if (travel <= 0) return;

        // 0 when the track's top reaches the viewport top, 1 when its bottom does
        var p = Math.min(1, Math.max(0, -r.top / travel));
        var i = Math.min(caps.length - 1, Math.floor(p * caps.length));

        for (var c = 0; c < caps.length; c++) {
          caps[c].classList.toggle('is-on', c === i);
        }
        for (var v = 0; v < clips.length; v++) {
          var on = (v === i);
          clips[v].classList.toggle('is-on', on);
          // Only the visible clip decodes. Playing four at once on a laptop
          // is the difference between smooth and a fan spinning up.
          if (on) { var pr = clips[v].play(); if (pr && pr.catch) pr.catch(function(){}); }
          else    { clips[v].pause(); }
        }
        for (var t = 0; t < ticks.length; t++) {
          var fill = ticks[t].querySelector('i');
          if (!fill) continue;
          fill.style.width = t < i ? '100%'
                           : t > i ? '0%'
                           : ((p * caps.length - i) * 100).toFixed(1) + '%';
        }
      }

      // A continuous rAF loop gated on visibility, NOT a scroll handler with a
      // latch. If a rAF callback is ever dropped — a throttled or backgrounded
      // tab — a latch stays raised and the scrub is dead for the life of the
      // page. A loop that starts and stops on visibility has no latch to stick,
      // and costs nothing while the act is off screen.
      var running = false;
      if ('IntersectionObserver' in window) {
        new IntersectionObserver(function (entries) {
          entries.forEach(function (e) {
            if (e.isIntersecting && !running) {
              running = true;
              window.requestAnimationFrame(frame);
            } else if (!e.isIntersecting) {
              running = false;
            }
          });
        }, { threshold: 0 }).observe(reel);
      }
      window.addEventListener('resize', scrub, { passive: true });
      scrub();
    });
  }

  // ------------------------------------------------------------ ground morph
  // Chapters change the page's light/dark register, transitioned rather than
  // cut. Discrete snap driven by IntersectionObserver, NOT a per-frame lerp:
  // the CSS transition already owns the 400ms morph, and a rAF loop
  // interpolating the same colour would fight it.
  //
  // THE BAND IS THE WHOLE TRICK. rootMargin '-50% 0px -50% 0px' with
  // threshold 0 collapses the root to a zero-height line at viewport centre, so
  // a chapter is "intersecting" exactly while it crosses mid-screen — at ANY
  // height. threshold: 0.5 is the bug that kills this silently: the pinned
  // chapters here are 220-330vh, so their ratio peaks near 0.3 and the observer
  // never fires once. The page then renders correctly and never morphs, which
  // is why it survives a screenshot review.
  function initMorph(root) {
    var chapters = root.querySelectorAll('[data-ef-ground]');
    if (!chapters.length) return;

    // Group the chapters by the morph container that owns them — <html> on a
    // real page, or any element carrying data-ef-morph on a scoped one. A
    // chapter with no such ancestor is opted out and simply paints its own band.
    var groups = [];
    Array.prototype.forEach.call(chapters, function (c) {
      var host = c.closest ? c.closest('[data-ef-morph]') : null;
      if (!host) return;
      var g = null;
      groups.forEach(function (x) { if (x.host === host) g = x; });
      if (!g) { g = { host: host, chapters: [] }; groups.push(g); }
      g.chapters.push(c);
    });
    if (!groups.length) return;

    groups.forEach(function (g) {
      function apply(ground) {
        g.host.setAttribute('data-theme', ground === 'dark' ? 'dark' : 'light');
        g.host.setAttribute('data-ef-morph', ground);
      }

      if (!('IntersectionObserver' in window)) {
        // No observer, no morph: drop the opt-in so every chapter falls back to
        // painting its own ground, exactly as the .no-js path does.
        g.host.removeAttribute('data-ef-morph');
        return;
      }

      // Paint the first chapter's ground before the observer's first callback,
      // or the page shows the document's default theme for one frame.
      apply(g.chapters[0].getAttribute('data-ef-ground'));

      var io = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            apply(entry.target.getAttribute('data-ef-ground'));
          }
        });
      }, { rootMargin: '-50% 0px -50% 0px', threshold: 0 });
      g.chapters.forEach(function (c) { io.observe(c); });
    });
  }

  // ------------------------------------------------------------ float pausing
  // The float is CSS and runs without this. All this does is stop paying for
  // the ones nobody is looking at.
  function initFloat(root) {
    var floats = root.querySelectorAll('.ef-float');
    if (!floats.length || reduce || !('IntersectionObserver' in window)) return;
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        entry.target.classList.toggle('is-out', !entry.isIntersecting);
      });
    }, { rootMargin: '10% 0px' });
    Array.prototype.forEach.call(floats, function (f) { io.observe(f); });
  }

  // The damped scroll-progress engine is DOCUMENT-scoped and idempotent: it
  // binds every [data-ef-scene] once. Re-running it per section-load would
  // stack rAF loops, so it is called after init rather than inside it.
  var sceneEngineStarted = false;
  function initScenes() {
    if (sceneEngineStarted) return;
    sceneEngineStarted = true;
  // ---- the aliveness primitive: continuous DAMPED scroll-progress ---------
    // The difference between this and the .ef-in reveals is not degree, it is
    // kind. A reveal is a boolean that fires once and cannot un-fire. This binds
    // transforms to a continuous 0..1 progress through a chapter, so it is
    // REVERSIBLE (scroll up and the scene runs backwards) and MULTI-RATE (one
    // scroll source drives several elements at different speeds, which is what
    // makes depth).
    //
    // NOT scroll-jacking. It smooths the READ value only — never preventDefault,
    // never overflow:hidden on the body, never moves content by hand. Native
    // scrollbar, iOS rubber-band, trackpad momentum and assistive tech all stay
    // exactly as they were; only the transforms trail behind via the lerp.
      (function () {
      const scenes = [...document.querySelectorAll('[data-ef-scene]')];
      if (!scenes.length) return;
      if (matchMedia('(prefers-reduced-motion: reduce)').matches) return;  // CSS
                                            // already collapses the pin track

      const DAMPING = 0.1;                  // 0.05 laggy · 0.1 Lenis-default · 0.15 snappy
      const clamp01 = v => v < 0 ? 0 : v > 1 ? 1 : v;
      const map = (t, a, b) => a + clamp01(t) * (b - a);

      // Parse "from,to" once. Reading these per frame would be the same mistake
      // as calling getBoundingClientRect per frame.
      const bind = [];
      for (const scene of scenes) {
        for (const el of scene.querySelectorAll('[data-ef-move],[data-ef-scale],[data-ef-fade]')) {
          const pair = a => { const v = el.dataset[a]; if (!v) return null;
                              const p = v.split(',').map(Number);
                              return p.length === 2 && p.every(n => !isNaN(n)) ? p : null; };
          bind.push({ scene, el, move: pair('efMove'), scale: pair('efScale'), fade: pair('efFade') });
        }
      }

      // Chaptered copy over a pinned plate, and the film under it scrubbed by
      // the same progress. Both read the scene's 0..1 the transforms already
      // use, so the words, the rail and the frame can never disagree.
      const chapters = [], scrubs = [];
      for (const scene of scenes) {
        const box = scene.querySelector('[data-ef-chapters]');
        if (box && box.children.length > 1) {
          chapters.push({ scene, items: [...box.children],
                          ticks: [...scene.querySelectorAll('[data-ef-chapterrail] i > b')] });
        }
        const v = scene.querySelector('video[data-ef-scrub]');
        if (v) scrubs.push({ scene, v });
      }
      if (!bind.length && !chapters.length && !scrubs.length) return;

      // offsetTop/offsetHeight are document-relative and do not force a reflow
      // the way getBoundingClientRect does inside a rAF loop. Cache, refresh on
      // resize — the numbers only change when layout does.
      let ranges = new Map();
      function measure() {
        ranges = new Map();
        for (const s of scenes) {
          const start = s.offsetTop;
          const span = s.offsetHeight - innerHeight;
          ranges.set(s, [start, span > 0 ? span : 1]);
        }
      }

      let target = scrollY, smoothed = scrollY, running = false;
      addEventListener('scroll', () => { target = scrollY; start(); }, { passive: true });
      addEventListener('resize', () => { measure(); start(); }, { passive: true });

      function frame() {
        smoothed += (target - smoothed) * DAMPING;
        for (const b of bind) {
          const [start0, span] = ranges.get(b.scene);
          const p = clamp01((smoothed - start0) / span);
          let t = "";
          if (b.move)  t += `translate3d(0,${map(p, b.move[0], b.move[1]).toFixed(2)}px,0)`;
          if (b.scale) t += ` scale(${map(p, b.scale[0], b.scale[1]).toFixed(4)})`;
          if (t) b.el.style.transform = t;
          if (b.fade) b.el.style.opacity = map(p, b.fade[0], b.fade[1]).toFixed(3);
        }
        for (const c of chapters) {
          const [c0, cspan] = ranges.get(c.scene);
          const p = clamp01((smoothed - c0) / cspan);
          const n = c.items.length;
          // The last chapter has to hold at p === 1 rather than fall off the
          // end of the array, which is what Math.min guards.
          const i = Math.min(n - 1, Math.floor(p * n));
          for (let k = 0; k < n; k++) c.items[k].classList.toggle('is-on', k === i);
          for (let k = 0; k < c.ticks.length; k++)
            c.ticks[k].style.width = k < i ? '100%' : k > i ? '0%'
                                   : ((p * n - i) * 100).toFixed(1) + '%';
        }
        for (const s of scrubs) {
          const [s0, sspan] = ranges.get(s.scene);
          const p = clamp01((smoothed - s0) / sspan);
          // readyState 2 = HAVE_CURRENT_DATA. Seeking before that throws the
          // frame away and the scrub sits on black. The 0.04 backs off the very
          // last frame, which some decoders will not render.
          if (s.v.readyState >= 2 && s.v.duration) {
            const want = p * (s.v.duration - 0.04);
            if (Math.abs(s.v.currentTime - want) > 0.02) s.v.currentTime = want;
          }
        }
        // Settle rather than spin forever: once the shadow value has caught up
        // there is nothing to interpolate, so stop until the next scroll.
        if (Math.abs(target - smoothed) > 0.15) requestAnimationFrame(frame);
        else { smoothed = target; running = false; }
      }
      function start() { if (!running) { running = true; requestAnimationFrame(frame); } }

      measure();
      smoothed = target = scrollY;
      start();
    })();

  }

  // ------------------------------------------------------- the pair picker
  // Progressive enhancement over a real GET form. Without this the submit
  // button posts ?a=..&b=.. and the template resolves it; with it, the reader
  // goes straight to the canonical pair URL, which is the page a crawler will
  // have indexed. Never a client-side column swap — that would leave one URL
  // trying to rank for every pair.
  function initPairPicker(root) {
    var forms = root.querySelectorAll('[data-ef-pairpick]');
    Array.prototype.forEach.call(forms, function (form) {
      if (form.dataset.efBound) return;
      form.dataset.efBound = '1';
      var A = form.querySelector('[data-ef-pair-a]');
      var B = form.querySelector('[data-ef-pair-b]');
      var base = form.getAttribute('data-ef-base') || '/pages/compare-';
      if (!A || !B) return;

      function url() { return base + A.value + '-vs-' + B.value; }

      // A model cannot be compared with itself. Move the OTHER side, so the
      // one the reader just touched is the one that sticks.
      function sync(changed) {
        if (A.value !== B.value) return;
        var other = changed === A ? B : A;
        for (var i = 0; i < other.options.length; i++) {
          if (other.options[i].value !== A.value) { other.value = other.options[i].value; break; }
        }
      }
      A.addEventListener('change', function () { sync(A); window.location.assign(url()); });
      B.addEventListener('change', function () { sync(B); window.location.assign(url()); });
      form.addEventListener('submit', function (e) {
        e.preventDefault(); sync(A); window.location.assign(url());
      });
    });
  }

  // ---------------------------------------------------------- the calculator
  // One step at a time, advancing in place with ?step=N so Back works and a
  // step is linkable. Sizing gates on SURGE as well as capacity: a unit that
  // cannot START a compressor is useless however large its battery.
  function initCalculator(root) {
    var forms = root.querySelectorAll('[data-ef-calc]');
    Array.prototype.forEach.call(forms, function (form) {
      if (form.dataset.efBound) return;
      form.dataset.efBound = '1';

      var models = [];
      try { models = JSON.parse('[' + (form.getAttribute('data-ef-models') || '') + ']'); } catch (e) {}
      var steps = [].slice.call(form.querySelectorAll('.ef-step'));
      var marks = [].slice.call(document.querySelectorAll('[data-ef-calc-steps] .ef-steps__i'));
      var next = form.querySelector('[data-ef-calc-next]');
      var back = form.querySelector('[data-ef-calc-back]');
      var again = form.querySelector('[data-ef-calc-restart]');
      var LAST = steps.length;
      var EFF = 0.85;
      var q = function (sel) { return form.querySelector(sel) || document.querySelector(sel); };

      function loads() {
        var w = 0, surge = 0;
        var boxes = form.querySelectorAll('[data-ef-calc-appl] input[type="checkbox"]');
        Array.prototype.forEach.call(boxes, function (i) {
          if (!i.checked) return;
          w += Number(i.dataset.w || 0);
          surge += Number(i.dataset.surge || i.dataset.w || 0);
        });
        return { w: w, surge: surge };
      }
      function hours() {
        var h = form.querySelector('input[name="hours"]:checked');
        return h ? Number(h.value) : 6;
      }
      function fmt(n) { return n.toLocaleString('en-MY'); }

      function tally() {
        var l = loads();
        var a = q('[data-ef-calc-w]'), b = q('[data-ef-calc-surge]');
        if (a) a.textContent = fmt(l.w);
        if (b) b.textContent = fmt(l.surge);
        // Zero watts is not an answer — block Next rather than sizing for nothing.
        if (current === 2 && next) next.disabled = l.w === 0;
      }

      function recommend() {
        var l = loads(), h = hours();
        var need = Math.round(l.w * h / EFF);
        var pick = null;
        for (var i = 0; i < models.length; i++) {
          var m = models[i];
          if (m.wh >= need && m.surge >= l.surge && m.ac >= l.w) { pick = m; break; }
        }
        var expand = !pick;
        if (!pick) pick = models[models.length - 1] || { name: '—', price: '', url: '#', spec: '' };
        var set = function (sel, txt) { var e = q(sel); if (e) e.textContent = txt; };
        set('[data-ef-calc-name]', pick.name);
        set('[data-ef-calc-spec]', pick.wh ? (fmt(pick.wh) + 'Wh · ' + fmt(pick.ac) + 'W · ' + fmt(pick.surge) + 'W surge') : '');
        set('[data-ef-calc-price]', pick.price || '');
        set('[data-ef-calc-sum]', fmt(l.w) + 'W for ' + h + ' hours = ' + fmt(need) + 'Wh, peak ' + fmt(l.surge) + 'W');
        set('[data-ef-calc-why]', expand
          ? 'Your selection needs more than one unit holds, so this is the model that expands — add batteries as the load grows.'
          : 'Runs your selection for about ' + h + ' hours, and starts every load on it including the surge.');
        var shop = q('[data-ef-calc-shop]');
        if (shop && pick.url) shop.setAttribute('href', pick.url);
      }

      var current = 1;
      function show(n, push) {
        current = Math.min(LAST, Math.max(1, n));
        steps.forEach(function (s) { s.hidden = Number(s.dataset.step) !== current; });
        marks.forEach(function (m) {
          var i = Number(m.dataset.step);
          if (i === current) m.setAttribute('aria-current', 'step'); else m.removeAttribute('aria-current');
          m.classList.toggle('is-done', i < current);
        });
        if (back)  back.hidden = current === 1 || current === LAST;
        if (next)  { next.hidden = current === LAST;
                     next.textContent = current === LAST - 1 ? 'See my match' : 'Next';
                     next.disabled = false; }
        if (again) again.hidden = current !== LAST;
        if (current === 2) tally();
        if (current === LAST) recommend();
        // Move focus to the step, or a keyboard user stays on a button that
        // just changed meaning underneath them.
        var lg = steps[current - 1] && steps[current - 1].querySelector('legend');
        if (lg) { lg.setAttribute('tabindex', '-1'); lg.focus({ preventScroll: true }); }
        if (push && window.history) history.pushState({ step: current }, '', '?step=' + current);
      }

      if (next)  next.addEventListener('click', function () { show(current + 1, true); });
      if (back)  back.addEventListener('click', function () { show(current - 1, true); });
      if (again) again.addEventListener('click', function () { show(1, true); });
      form.addEventListener('change', tally);
      window.addEventListener('popstate', function (e) { show((e.state && e.state.step) || 1, false); });

      var start = Number(new URLSearchParams(location.search).get('step')) || 1;
      show(start, false);
    });
  }

  function init(root) {
    root = root || document;
    initReveals(root);
    initFilm(root);
    initReels(root);
    initMorph(root);
    initFloat(root);
    initPairPicker(root);
    initCalculator(root);
    initScenes();
  }

  document.documentElement.classList.remove('no-js');
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { init(document); });
  } else {
    init(document);
  }

  // Shopify Online Store 2.0: re-init when a merchant adds or edits a section
  // in the theme editor, or the reveals never fire on the block they just added.
  document.addEventListener('shopify:section:load', function (e) { init(e.target); });
  document.addEventListener('shopify:section:select', function (e) { init(e.target); });
})();
